import React, { useState } from 'react';
import './productMainUsp.css';
import { useRecoilState } from 'recoil';
import { Label, Icon } from '@nooz-optics/nooz-components-lib';
import { IBlock } from '../../../../../utils/types/contentful';
import { formatContentfulComponentProps } from '../../../../../utils/globals';
import { List } from '../../../../UI/Navigation/List/list';
import { modalState } from '../../../../../utils/recoil/modal';
import { MainSplitBodyPopup } from '../../../../Marketing/Popup';
import { useMediaQuery } from '../../../../../utils/hooks/useMediaQuery';
import { SolidityHammer } from '../../../../../icons';
import clsx from 'clsx';
import { IShopUspProps } from 'components/Ecommerce/Shop/Usp';
import { sidebarState } from '../../../../../utils/recoil/sidebar';
import { Body, Button } from '../../../../UI/Elements';
import { Image } from '../../../../UI/Media';
import { useDrawer } from 'utils/recoil/drawer';

export interface ProductMainUspProps {
  usps: IBlock;
}

const ProductMainUspItem = (item) => {
  const drawer = useDrawer();
  const [isHover, setIsHover] = useState(false);

  const { icon, label, subLabel } = item;

  const handleOnClick = () => {
    const { image, title, body } = formatContentfulComponentProps(item.modal);
    drawer.open({
      placement: 'right',
      closeButton: <Button icon={{ iconKey: 'HiOutlineX', size: 'base' }} />,
      title: null,
      actionButton: null,
      width: 'lg:w-screen/3 w-screen-95',
      body: (
        <div className='p-4'>
          <div className='relative flex flex-col h-auto'>
            <div className='h-72 overflow-hidden w-full'>
              <Image
                {...image}
                objectFit='cover'
                width='100%'
                height='100%'
              />
            </div>
            <div className='h-1/2 mt-4 flex flex-col justify-center text-left items-start'>
              <Body {...body} />
            </div>
          </div>
        </div>
      ),
      footer: null,
      backdropLink: null,
    });
  };

  return (
    <button
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      onClick={handleOnClick}
      type='button'
      className='w-full lea flex justify-center items-center md:w-min md:items-start md:flex-row cursor-pointer'>
      <div
        className={`product-main-usp bg-white flex items-center justify-center border rounded-full mr-3 md:mr-5 w-10 h-10 p-1 md:flex ${
          isHover ? `border-${icon?.color?.classname}` : 'border-gray-400'
        } transform duration-300 ease-in-out`}>
        <Icon {...icon} />
      </div>
      <div className='flex w-full md:w-auto item-center flex-row justify-between text-right items-start md:flex-col md:items-start'>
        <Label
          text={label?.text}
          lineHeight={4}
          color={isHover ? { classname: 'black' } : { classname: 'gray-800' }}
          weight='medium'
          size='base'
          truncate
        />
        <Label
          text={subLabel?.text}
          color={isHover ? { classname: 'black' } : label.color}
          icon={{
            iconKey: 'FaQuestionCircle',
            size: 'xs',
            color: { classname: isHover ? 'black' : 'gray-400' },
          }}
          weight='light'
          family='font-inter'
          truncate
          size='sm'
        />
      </div>
    </button>
  );
};

export const ProductMainUsp: React.FC<IShopUspProps> = ({ usps }) => {
  const uspList = usps && usps.items;
  return (
    <div className='w-full flex flex-wrap gap-6 items-center'>
      {uspList?.map((item) => (
        <ProductMainUspItem {...formatContentfulComponentProps(item)} />
      ))}
    </div>
  );
};

const CategoryMainUspItem = (item) => {
  const drawer = useDrawer();
  const [isHover, setIsHover] = useState(false);

  const { icon, label, subLabel } = item;

  const handleOnClick = () => {
    const { image, title, body } = formatContentfulComponentProps(item.modal);
    drawer.open({
      placement: 'right',
      closeButton: <Button icon={{ iconKey: 'HiOutlineX', size: 'base' }} />,
      title: null,
      actionButton: null,
      width: 'lg:w-screen/3 w-screen-95',
      body: (
        <div className='p-4'>
          <div className='relative flex flex-col h-auto'>
            <div className='h-72 overflow-hidden w-full'>
              <Image
                {...image}
                objectFit='cover'
                width='100%'
                height='100%'
              />
            </div>
            <div className='h-1/2 mt-4 flex flex-col justify-center text-left items-start'>
              <Body {...body} />
            </div>
          </div>
        </div>
      ),
      footer: null,
      backdropLink: null,
    });
  };

  return (
    <button
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      onClick={handleOnClick}
      type='button'
      className='flex px-1 py-1 md:px-2 md:py-2 justify-center items-center flex-row cursor-pointer w-full '>
      <div
        className={`flex items-center justify-center w-4 mr-3 md:flex transform duration-300 ease-in-out ${
          isHover ? 'text-black' : 'text-reading-light'
        }`}>
        <Icon
          {...icon}
          size='base'
        />
      </div>
      <div className='flex w-full md:w-auto item-center flex-row text-right items-start items-center'>
        <Label
          text={label?.text}
          color={{ classname: 'black' }}
          weight='normal'
          size='base'
          truncate
        />
        <div className={'ml-1 block'}>
          <Icon
            {...{
              iconKey: 'FaQuestionCircle',
              size: 'base',
              color: { classname: isHover ? 'black' : 'reading-light' },
            }}
          />
        </div>
      </div>
    </button>
  );
};

export const CategoryMainUsp: React.FC<IShopUspProps> = ({ usps }) => {
  const shopUspHeight = 'xl:h-24';
  const uspList = usps && usps.items;

  const isWindowSmallSize = useMediaQuery('sm');

  return (
    <List
      className={'flex flex-col md:flex-row justify-center'}
      classNameItem={'flex w-auto px-2'}
      full={false}
      isHorizontal={!isWindowSmallSize}
      listItems={uspList?.map((item) => ({
        children: (
          <CategoryMainUspItem
            key={item?.items?.[0]?.id}
            {...formatContentfulComponentProps(item)}
          />
        ),
      }))}
    />
  );
};
